import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FaSearch,
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaQuestion,
  FaGraduationCap,
  FaExclamationTriangle,
  FaDownload,
  FaSyncAlt,
  FaTimes,
  FaInfoCircle,
  FaSortAmountDown,
  FaSortAmountUp
} from 'react-icons/fa';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
  BarElement, Title, PointElement, LineElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement);

const colorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

/* Score bands mirror utils/studentScoring.js on the server. */
const TONES = {
  excellent: { text: 'text-green-700',  bg: 'bg-green-100',  bar: 'bg-green-500',  ring: 'border-green-300' },
  good:      { text: 'text-teal-700',   bg: 'bg-teal-100',   bar: 'bg-teal-500',   ring: 'border-teal-300' },
  fair:      { text: 'text-amber-700',  bg: 'bg-amber-100',  bar: 'bg-amber-500',  ring: 'border-amber-300' },
  weak:      { text: 'text-orange-700', bg: 'bg-orange-100', bar: 'bg-orange-500', ring: 'border-orange-300' },
  risk:      { text: 'text-red-700',    bg: 'bg-red-100',    bar: 'bg-red-500',    ring: 'border-red-300' },
  none:      { text: 'text-gray-500',   bg: 'bg-gray-100',   bar: 'bg-gray-300',   ring: 'border-gray-200' }
};

const RANGE_OPTIONS = [
  { value: 'all',  label: 'All time' },
  { value: '365d', label: 'Last 12 months' },
  { value: '180d', label: 'Last 6 months' },
  { value: '90d',  label: 'Last 90 days' },
  { value: '30d',  label: 'Last 30 days' }
];

const SORT_OPTIONS = [
  { value: 'rank',        label: 'Score' },
  { value: 'name',        label: 'Name' },
  { value: 'submitted',   label: 'Submissions' },
  { value: 'accepted',    label: 'Accepted' },
  { value: 'declined',    label: 'Declined' },
  { value: 'pending',     label: 'Pending' },
  { value: 'acceptance',  label: 'Acceptance rate' }
];

// Rounded defensively: the API already rounds, but a raw float must never reach the screen.
const fmtPct = (value) => (value === null || value === undefined ? '—' : `${Math.round(value * 10) / 10}%`);
const fmtScore = (value) => (value === null || value === undefined ? '—' : Number(value).toFixed(1));
const fmtNum = (value) => (value === null || value === undefined ? '—' : value);
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toneOf = (student) => TONES[student.tone] || TONES.none;

const TrendIcon = ({ trend }) => {
  const direction = trend?.direction || 'unknown';
  const delta = trend?.deltaPct;
  const title = direction === 'unknown'
    ? 'Not enough decided reviews in both windows to compare'
    : direction === 'stable'
      ? 'Acceptance rate steady versus the previous 60 days'
      : `Acceptance rate ${direction === 'up' ? 'up' : 'down'} ${Math.abs(delta)} points versus the previous 60 days`;

  const map = {
    up: <FaArrowUp className="h-3 w-3 text-green-600" />,
    down: <FaArrowDown className="h-3 w-3 text-red-600" />,
    stable: <FaMinus className="h-3 w-3 text-gray-400" />,
    unknown: <FaQuestion className="h-3 w-3 text-gray-300" />
  };

  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" title={title}>
      {map[direction]}
      <span className="text-xs text-gray-500">
        {direction === 'unknown' || delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta}`}
      </span>
    </span>
  );
};

const GradeBadge = ({ student, size = 'sm' }) => {
  const tone = toneOf(student);
  const pad = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${pad} ${tone.bg} ${tone.text}`}>
      {student.grade || '–'}
      <span className="font-normal opacity-80">{student.gradeLabel}</span>
    </span>
  );
};

const ScoreBar = ({ score, tone }) => (
  <div className="w-full bg-gray-200 rounded-full h-1.5" role="presentation">
    <div
      className={`h-1.5 rounded-full transition-all duration-500 ${tone.bar}`}
      style={{ width: `${Math.max(0, Math.min(100, score || 0))}%` }}
    />
  </div>
);

const SummaryTile = ({ label, value, sub, accent }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: accent || colorPalette.text }}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
  </div>
);

/* ------------------------------------------------------------------ scorecard */

const ScorecardModal = ({ studentId, range, target, token, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/supervisors/students/${encodeURIComponent(studentId)}`,
          { headers: { Authorization: `Bearer ${token}` }, params: { range, target } }
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load this scorecard. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studentId, range, target, token]);

  const chart = useMemo(() => {
    if (!data?.monthly?.length) return null;
    return {
      data: {
        labels: data.monthly.map(m => m.label),
        datasets: [
          {
            label: 'Accepted',
            data: data.monthly.map(m => m.accepted),
            backgroundColor: 'rgba(40, 167, 69, 0.85)',
            stack: 'outcome'
          },
          {
            label: 'Declined',
            data: data.monthly.map(m => m.declined),
            backgroundColor: 'rgba(191, 59, 65, 0.85)',
            stack: 'outcome'
          },
          {
            label: 'Pending',
            data: data.monthly.map(m => m.pending),
            backgroundColor: 'rgba(148, 163, 184, 0.7)',
            stack: 'outcome'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Submissions' } }
        }
      }
    };
  }, [data]);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-4"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {data?.student?.name || 'Student scorecard'}
            </h2>
            <p className="text-sm text-gray-500 truncate">
              {data?.student?.studentId ? `ID ${data.student.studentId}` : studentId}
              {data?.student?.email ? ` · ${data.student.email}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close scorecard"
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        {loading && (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colorPalette.primary }} />
          </div>
        )}

        {!loading && error && (
          <div className="p-6">
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <FaExclamationTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <div className="p-5 space-y-6">
            {/* headline score */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-xl border border-gray-200 p-4">
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-bold tabular-nums ${toneOf(data).text}`}>{fmtScore(data.score)}</span>
                <span className="text-sm text-gray-400">/ 100</span>
              </div>
              <div className="flex-1 space-y-2">
                <ScoreBar score={data.score} tone={toneOf(data)} />
                <div className="flex flex-wrap items-center gap-2">
                  <GradeBadge student={data} size="lg" />
                  {data.provisional && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      <FaInfoCircle className="h-3 w-3" /> Provisional
                    </span>
                  )}
                  <TrendIcon trend={data.trend} />
                </div>
              </div>
            </div>

            {data.provisional && (
              <p className="text-xs text-gray-500 -mt-3">
                Provisional: this score rests on {data.metrics.submitted} submission{data.metrics.submitted === 1 ? '' : 's'} and
                {' '}{data.measuredWeight} of 100 weight points of measurable evidence. It will firm up as more work is reviewed.
              </p>
            )}

            {/* how the score was built */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">How this score was built</h3>
              <div className="space-y-3">
                {data.components.map(component => (
                  <div key={component.key}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="text-gray-700">
                        {component.label}
                        <span className="ml-1.5 text-xs text-gray-400">weight {component.weight}</span>
                      </span>
                      <span className="tabular-nums text-gray-600">
                        {component.measured
                          ? <><strong className="text-gray-900">{component.points}</strong> / {component.maxPoints} pts · {fmtPct(component.ratePct)}</>
                          : <em className="text-gray-400 not-italic">not measured yet</em>}
                      </span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${component.measured ? 'bg-blue-500' : 'bg-gray-300'}`}
                        style={{ width: `${component.measured ? component.ratePct : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Components with no data yet are excluded and the remaining weights are rescaled to 100, so the score
                always reflects what has actually been reviewed.
              </p>
            </section>

            {/* review counts */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryTile label="Submitted" value={fmtNum(data.metrics.submitted)} />
              {/* A zero is left untinted - colouring it implies a signal that is not there. */}
              <SummaryTile label="Accepted" value={fmtNum(data.metrics.accepted)} accent={data.metrics.accepted ? '#1B8F52' : undefined} />
              <SummaryTile label="Declined" value={fmtNum(data.metrics.declined)} accent={data.metrics.declined ? '#BF3B41' : undefined} />
              <SummaryTile label="Pending" value={fmtNum(data.metrics.pending)} accent={data.metrics.pending ? '#C07A10' : undefined} />
            </section>

            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryTile
                label="Steps approved"
                value={fmtPct(data.metrics.stepApprovalRatePct)}
                sub={`${data.metrics.acceptedSteps} of ${data.metrics.gradedSteps} graded steps`}
              />
              <SummaryTile
                label="Documentation"
                value={fmtPct(data.metrics.documentationRatePct)}
                sub={`${data.metrics.stepsTicked} of ${data.metrics.stepsTotal} steps ticked`}
              />
              <SummaryTile
                label="Resubmitted cases"
                value={fmtPct(data.metrics.resubmissionRatePct)}
                sub={`${data.metrics.resubmittedCases} of ${data.metrics.cases} cases`}
              />
              <SummaryTile
                label="Days to decision"
                value={data.metrics.avgDaysToDecision === null ? '—' : data.metrics.avgDaysToDecision}
                sub="average, submission to decision"
              />
            </section>

            {/* monthly outcomes */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Submissions and outcomes by month</h3>
              {chart ? (
                <div className="h-64"><Bar data={chart.data} options={chart.options} /></div>
              ) : (
                <p className="text-sm text-gray-500">No submissions in this period.</p>
              )}
            </section>

            {/* per discipline */}
            {data.byProcedure?.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">By discipline</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Discipline</th>
                        <th className="px-3 py-2 text-right font-medium">Submitted</th>
                        <th className="px-3 py-2 text-right font-medium">Accepted</th>
                        <th className="px-3 py-2 text-right font-medium">Declined</th>
                        <th className="px-3 py-2 text-right font-medium">Pending</th>
                        <th className="px-3 py-2 text-right font-medium">Acceptance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.byProcedure.map(row => (
                        <tr key={row.procedureType}>
                          <td className="px-3 py-2 font-medium text-gray-900">{row.procedureType}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{row.submitted}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${row.accepted ? 'text-green-700' : 'text-gray-400'}`}>{row.accepted}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${row.declined ? 'text-red-700' : 'text-gray-400'}`}>{row.declined}</td>
                          <td className={`px-3 py-2 text-right tabular-nums ${row.pending ? 'text-amber-700' : 'text-gray-400'}`}>{row.pending}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtPct(row.acceptanceRatePct)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* recent submissions */}
            {data.recentReviews?.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Recent submissions <span className="font-normal text-gray-400">(most recent {data.recentReviews.length})</span>
                </h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200 max-h-72 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Submitted</th>
                        <th className="px-3 py-2 text-left font-medium">Procedure</th>
                        <th className="px-3 py-2 text-left font-medium">Result</th>
                        <th className="px-3 py-2 text-left font-medium">Decided</th>
                        <th className="px-3 py-2 text-left font-medium">Faculty comment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.recentReviews.map(review => (
                        <tr key={review.id}>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(review.submittedDate)}</td>
                          <td className="px-3 py-2 text-gray-900">{review.procedureType}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                              review.status === 'accepted' ? 'bg-green-100 text-green-700'
                                : review.status === 'declined' ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {review.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(review.reviewedDate)}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={review.comment || ''}>
                            {review.comment || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

/* ------------------------------------------------------------------ main page */

const Students = () => {
  const { token } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rank');
  const [sortDesc, setSortDesc] = useState(true);
  const [range, setRange] = useState('all');
  const [target, setTarget] = useState(10);
  const [openStudentId, setOpenStudentId] = useState(null);

  const fetchScores = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/supervisors/students`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { range, target }
      });
      setPayload(res.data);
    } catch (err) {
      console.error('Error loading student scores:', err);
      setError(
        err.response?.data?.message ||
        'Could not load student scores. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, range, target]);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  // Memoised so the `visible` useMemo below is not invalidated on every render
  // by a fresh empty-array literal.
  const students = useMemo(() => payload?.students || [], [payload]);
  const summary = payload?.summary;
  const weights = payload?.scoring?.weights;

  const visible = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const filtered = students.filter(student => {
      if (gradeFilter === 'unscored' && student.score !== null) return false;
      if (gradeFilter === 'atRisk' && !(student.grade === 'D' || student.grade === 'F')) return false;
      if (!['all', 'unscored', 'atRisk'].includes(gradeFilter) && student.grade !== gradeFilter) return false;
      if (!needle) return true;
      return [student.name, student.studentId, student.email]
        .some(field => String(field || '').toLowerCase().includes(needle));
    });

    const value = (student) => {
      switch (sortBy) {
        case 'name':       return student.name || '';
        case 'submitted':  return student.metrics.submitted;
        case 'accepted':   return student.metrics.accepted;
        case 'declined':   return student.metrics.declined;
        case 'pending':    return student.metrics.pending;
        case 'acceptance': return student.metrics.acceptanceRatePct;
        default:           return student.score;
      }
    };

    return [...filtered].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      // Students with no score for this measure always sink to the bottom,
      // whichever direction is selected.
      if (av === null && bv === null) return (a.name || '').localeCompare(b.name || '');
      if (av === null) return 1;
      if (bv === null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDesc ? -cmp : cmp;
    });
  }, [students, searchTerm, gradeFilter, sortBy, sortDesc]);

  const exportCsv = () => {
    const header = [
      'Rank', 'Name', 'Student ID', 'Email', 'Submitted', 'Accepted', 'Declined', 'Pending',
      'Acceptance %', 'Step approval %', 'Documentation %', 'Volume %', 'Resubmitted cases',
      'Avg days to decision', 'Score', 'Grade', 'Provisional'
    ];
    const rows = visible.map(s => [
      s.rank ?? '', s.name, s.studentId, s.email,
      s.metrics.submitted, s.metrics.accepted, s.metrics.declined, s.metrics.pending,
      s.metrics.acceptanceRatePct ?? '', s.metrics.stepApprovalRatePct ?? '',
      s.metrics.documentationRatePct ?? '', s.metrics.volumeRatePct ?? '',
      s.metrics.resubmittedCases, s.metrics.avgDaysToDecision ?? '',
      s.score ?? '', s.grade ?? '', s.provisional ? 'yes' : 'no'
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `student-scores-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: colorPalette.primary }} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* header + controls */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Students</h1>
            <p className="text-sm text-gray-600 mt-1">
              Performance scores built from submitted reviews and your faculty decisions.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">Period</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600" title="Submissions that count as a full clinical load">
                Volume target
              </span>
              <input
                type="number"
                min="1"
                max="200"
                value={target}
                onChange={(e) => setTarget(Math.max(1, Number(e.target.value) || 1))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </label>
            <button
              onClick={() => fetchScores({ silent: true })}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FaSyncAlt className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              disabled={!visible.length}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ backgroundColor: colorPalette.primary }}
            >
              <FaDownload className="h-3.5 w-3.5" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <FaExclamationTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Student scores unavailable</p>
            <p className="mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => fetchScores()}
            className="px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 text-xs font-medium hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      {/* cohort summary */}
      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <SummaryTile
              label="Students"
              value={summary.students}
              sub={summary.studentsWithoutSubmissions
                ? `${summary.studentsWithoutSubmissions} with no submissions`
                : 'all have submitted'}
            />
            <SummaryTile
              label="Submissions"
              value={summary.submitted}
              sub={`${summary.pending} still pending`}
              accent={colorPalette.primary}
            />
            <SummaryTile
              label="Acceptance rate"
              value={fmtPct(summary.acceptanceRatePct)}
              sub={`${summary.accepted} accepted of ${summary.decided} decided`}
              accent="#1B8F52"
            />
            <SummaryTile
              label="Average score"
              value={fmtScore(summary.averageScore)}
              sub={`median ${fmtScore(summary.medianScore)}`}
              accent={colorPalette.secondary}
            />
            <SummaryTile
              label="Needing support"
              value={summary.atRisk}
              sub="graded D or F"
              accent={summary.atRisk > 0 ? '#BF3B41' : undefined}
            />
          </div>

          {/* grade distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-900">Grade distribution</h2>
              {weights && (
                <p className="text-xs text-gray-500">
                  Score = acceptance {weights.acceptance} · steps {weights.stepApproval} · documentation {weights.documentation} · volume {weights.volume}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.gradeDistribution).map(([grade, count]) => {
                const tone = TONES[
                  grade === 'A' ? 'excellent' : grade === 'B' ? 'good' : grade === 'C' ? 'fair' : grade === 'D' ? 'weak' : 'risk'
                ];
                const active = gradeFilter === grade;
                return (
                  <button
                    key={grade}
                    onClick={() => setGradeFilter(active ? 'all' : grade)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      active ? `${tone.bg} ${tone.ring}` : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`font-semibold ${tone.text}`}>{grade}</span>
                    <span className="tabular-nums text-gray-700">{count}</span>
                  </button>
                );
              })}
              {summary.studentsWithoutSubmissions > 0 && (
                <button
                  onClick={() => setGradeFilter(gradeFilter === 'unscored' ? 'all' : 'unscored')}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                    gradeFilter === 'unscored' ? 'bg-gray-100 border-gray-300' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium text-gray-500">No submissions</span>
                  <span className="tabular-nums text-gray-700">{summary.studentsWithoutSubmissions}</span>
                </button>
              )}
              {summary.atRisk > 0 && (
                <button
                  onClick={() => setGradeFilter(gradeFilter === 'atRisk' ? 'all' : 'atRisk')}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                    gradeFilter === 'atRisk' ? 'bg-red-100 border-red-300' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <FaExclamationTriangle className="h-3 w-3 text-red-600" />
                  <span className="font-medium text-red-700">Needs support</span>
                  <span className="tabular-nums text-gray-700">{summary.atRisk}</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name, student ID or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button
              onClick={() => setSortDesc(v => !v)}
              title={sortDesc ? 'Highest first' : 'Lowest first'}
              className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {sortDesc ? <FaSortAmountDown className="h-4 w-4" /> : <FaSortAmountUp className="h-4 w-4" />}
            </button>
          </div>
          {(gradeFilter !== 'all' || searchTerm) && (
            <button
              onClick={() => { setGradeFilter('all'); setSearchTerm(''); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* scoreboard */}
      {visible.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium w-14" title="Rank across the whole cohort by score, whatever this table is sorted by">#</th>
                  <th className="px-4 py-3 text-left font-medium">Student</th>
                  <th className="px-3 py-3 text-right font-medium" title="Reviews the student has submitted">Sub&shy;mitted</th>
                  <th className="px-3 py-3 text-right font-medium">Accepted</th>
                  <th className="px-3 py-3 text-right font-medium">Declined</th>
                  <th className="px-3 py-3 text-right font-medium" title="Submitted but not yet decided by faculty">Pending</th>
                  <th className="px-3 py-3 text-right font-medium" title="Accepted as a share of decided reviews">Accep&shy;tance</th>
                  <th className="px-3 py-3 text-right font-medium" title="Share of individually graded review steps that were approved">Steps</th>
                  <th className="px-4 py-3 text-left font-medium w-44">Score</th>
                  <th className="px-3 py-3 text-left font-medium">Trend</th>
                  <th className="px-3 py-3 text-right font-medium w-12" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map(student => {
                  const tone = toneOf(student);
                  return (
                    <tr
                      key={student.id || student.studentId}
                      onClick={() => setOpenStudentId(student.studentId)}
                      className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 tabular-nums text-gray-400 font-medium">{student.rank ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className="h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                            style={{ background: `linear-gradient(135deg, ${colorPalette.primary}, ${colorPalette.secondary})` }}
                          >
                            {(student.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate">{student.name}</div>
                            <div className="text-xs text-gray-500 truncate">{student.studentId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-gray-900">{student.metrics.submitted}</td>
                      {/* A zero carries no signal, so it is not tinted. */}
                      <td className={`px-3 py-3 text-right tabular-nums ${student.metrics.accepted ? 'text-green-700' : 'text-gray-400'}`}>{student.metrics.accepted}</td>
                      <td className={`px-3 py-3 text-right tabular-nums ${student.metrics.declined ? 'text-red-700' : 'text-gray-400'}`}>{student.metrics.declined}</td>
                      <td className={`px-3 py-3 text-right tabular-nums ${student.metrics.pending ? 'text-amber-700' : 'text-gray-400'}`}>{student.metrics.pending}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-700">{fmtPct(student.metrics.acceptanceRatePct)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-700">{fmtPct(student.metrics.stepApprovalRatePct)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`w-11 text-right tabular-nums font-semibold ${tone.text}`}>{fmtScore(student.score)}</span>
                          <div className="flex-1 min-w-[52px]"><ScoreBar score={student.score} tone={tone} /></div>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <GradeBadge student={student} />
                          {student.provisional && (
                            <span
                              className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
                              title={`Based on ${student.metrics.submitted} submission(s) and ${student.measuredWeight}/100 weight points of evidence`}
                            >
                              provisional
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3"><TrendIcon trend={student.trend} /></td>
                      <td className="px-3 py-3 text-right">
                        <FaEye className="inline h-4 w-4 text-gray-300" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500">
            Showing {visible.length} of {students.length} students
            {payload?.generatedAt && ` · updated ${new Date(payload.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
          </div>
        </div>
      ) : (
        !error && (
          <div className="bg-white rounded-xl shadow-sm text-center py-12 px-6">
            <FaGraduationCap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">No students match these filters</h3>
            <p className="text-sm text-gray-600">
              {students.length === 0
                ? 'No students are registered for your university yet.'
                : 'Try clearing the search or grade filter, or widening the period.'}
            </p>
          </div>
        )
      )}

      {openStudentId && (
        <ScorecardModal
          studentId={openStudentId}
          range={range}
          target={target}
          token={token}
          onClose={() => setOpenStudentId(null)}
        />
      )}
    </div>
  );
};

export default Students;
